/**
 * Users Tab Component - Users list with search and add user
 */

import { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Loader2,
  Users,
  Search,
  UserPlus,
  MoreVertical,
  Shield,
  Phone,
  UserCog,
  Pencil,
  KeyRound,
  Star,
} from 'lucide-react';
import { UserProfile } from '../../lib/api';
import { AddUserDialog } from './AddUserDialog';

interface UsersTabProps {
  users: UserProfile[];
  filteredUsers: UserProfile[];
  usersLoading: boolean;
  userSearch: string;
  impersonating: number | null;
  isHelixAdmin: boolean;
  isCustomer: boolean;
  /** CR-5857: customer admin (or helix admin) — gates Edit + Reset Password. */
  isCustomerAdmin: boolean;
  companyId: number;
  companyName: string;
  groupId?: number | null;
  onUserSearchChange: (search: string) => void;
  onOpenUserModal: (userProfile: UserProfile) => Promise<void>;
  onImpersonate: (userProfile: UserProfile) => Promise<void>;
  onRefreshUsers: () => void;
  /** CR-5857: open the Edit Profile dialog for the given user. */
  onEditUser: (userProfile: UserProfile) => void;
  /** CR-5857: trigger an admin-driven password reset email. */
  onResetPassword: (userProfile: UserProfile) => void;
  /** CR-7255 review: why the list was refused, or null. `manages_own_users`
   *  = a customer site that also supplies you; its own admins run its users. */
  usersAccessCode?: string | null;
  /** The company's type, so Add User offers roles of the right side. */
  companyType?: string;
}

export function UsersTab({
  users,
  filteredUsers,
  usersLoading,
  userSearch,
  impersonating,
  isHelixAdmin,
  isCustomer,
  isCustomerAdmin,
  companyId,
  companyName,
  groupId,
  onUserSearchChange,
  onOpenUserModal,
  onImpersonate,
  onRefreshUsers,
  onEditUser,
  onResetPassword,
  usersAccessCode = null,
  companyType,
}: UsersTabProps) {
  const [addUserOpen, setAddUserOpen] = useState(false);
  if (usersAccessCode === 'manages_own_users') {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="users-managed-elsewhere">
        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p className="font-medium text-foreground">Users are managed by {companyName}.</p>
        <p className="mt-1 text-sm">
          {companyName} also uses the platform as a customer, so its own administrators add and
          change its users.
        </p>
      </div>
    );
  }
  /** True when this user is the designated primary contact for THIS supplier.
   *  Designations for other companies are deliberately not shown here — the
   *  badge answers "who do we call about this supplier". Set in Users
   *  Management → user → Primary Contact For. */
  const isPrimaryContact = (userProfile: UserProfile) =>
    (userProfile.primary_contact_for ?? []).some((a) => a.company === companyId);
  // CR-5857: Edit + Reset Password are admin actions — helix admins and
  // customer admins managing supplier users. Hide for everyone else; the
  // matching backend guard sits in CanManageUsers.has_object_permission.
  const canManageProfile = isHelixAdmin || isCustomerAdmin;
  return (
    <div className="space-y-4">
      {/* Header with search and actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search users..."
            value={userSearch}
            onChange={(e) => onUserSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        {/* A refused list means no authority to add users here either. */}
        {!usersAccessCode && (
          <div className="flex items-center gap-2">
            <Button variant="default" size="sm" onClick={() => setAddUserOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        )}
      </div>

      {usersLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{userSearch ? 'No users match your search' : 'No users found for this supplier'}</p>
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {filteredUsers.map((userProfile) => (
            <Card
              key={userProfile.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onOpenUserModal(userProfile)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-semibold text-primary">
                        {userProfile.user.first_name?.[0] ||
                          userProfile.user.email[0].toUpperCase()}
                        {userProfile.user.last_name?.[0] || ''}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {userProfile.user.full_name || userProfile.user.email.split('@')[0]}
                        {userProfile.role_details?.is_admin_role && (
                          <span title="Admin role" aria-label="Admin role">
                            <Shield className="h-4 w-4 text-amber-500" />
                          </span>
                        )}
                        {isPrimaryContact(userProfile) && (
                          <Star
                            className="h-4 w-4 text-amber-500 fill-amber-500"
                            aria-label="Primary contact"
                          />
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {userProfile.job_title || 'No title'}
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenUserModal(userProfile);
                        }}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      {canManageProfile && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditUser(userProfile);
                            }}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit User Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onResetPassword(userProfile);
                            }}
                            disabled={!userProfile.user.is_active}
                          >
                            <KeyRound className="h-4 w-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                        </>
                      )}
                      {(isHelixAdmin || isCustomer) && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onImpersonate(userProfile);
                            }}
                            disabled={
                              !userProfile.user.is_active || impersonating === userProfile.user.id
                            }
                          >
                            <UserCog className="h-4 w-4 mr-2" />
                            Login As
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="text-sm text-muted-foreground mb-3">{userProfile.user.email}</div>

                <div className="flex flex-wrap gap-2">
                  {isPrimaryContact(userProfile) && (
                    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-xs gap-1">
                      <Star className="h-3 w-3 fill-current" />
                      Primary Contact
                    </Badge>
                  )}
                  {userProfile.role_name && (
                    <Badge variant="secondary" className="text-xs">
                      {userProfile.role_name}
                    </Badge>
                  )}
                  {/* CR-4551 round 2: render an "Invite Sent" intermediate
                      state so the badge doesn't say "Active" while the
                      invitee hasn't accepted the invitation yet. */}
                  {(() => {
                    const u = userProfile.user;
                    if (!u.is_active) {
                      return (
                        <Badge variant="outline" className="text-red-600 border-red-600 text-xs">
                          Inactive
                        </Badge>
                      );
                    }
                    if (u.invitation_sent && !u.has_joined) {
                      return (
                        <Badge variant="outline" className="text-amber-600 border-amber-600 text-xs">
                          Invite Sent
                        </Badge>
                      );
                    }
                    // Provisioned but never invited — e.g. the primary contact
                    // created when the supplier was approved from the wizard.
                    // Without this the card read "Active" for an account with an
                    // unusable password that nobody can log into yet. Matches the
                    // "Not Invited" styling in the Users Management table.
                    if (!u.invitation_sent && !u.has_joined) {
                      return (
                        <Badge variant="outline" className="text-gray-500 border-gray-300 text-xs">
                          Not Invited
                        </Badge>
                      );
                    }
                    return (
                      <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                        Active
                      </Badge>
                    );
                  })()}
                  {!userProfile.is_approved && (
                    <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs">
                      Pending
                    </Badge>
                  )}
                </div>

                {userProfile.phone && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    {userProfile.phone}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* User count summary */}
      {users.length > 0 && (
        <div className="text-sm text-muted-foreground">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      )}

      {/* Add User Dialog */}
      <AddUserDialog
        open={addUserOpen}
        onOpenChange={setAddUserOpen}
        companyId={companyId}
        companyName={companyName}
        companyType={companyType}
        groupId={groupId}
        onUserAdded={onRefreshUsers}
      />
    </div>
  );
}
